import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight, Phone, Mail } from 'lucide-react';
import { getNavLinks, SERVICES, getContact } from '@/data/site';
import { useLiveTick } from '@/lib/liveContent';
import { ServiceMark } from './AmdenMark';
import { stopScroll, startScroll } from '@/lib/scroll';
import { cn } from '@/lib/utils';

const DesktopLink = ({ link }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const base =
    'relative inline-flex items-center gap-1 px-1 py-2 text-sm font-semibold tracking-wide transition-colors';
  if (link.hasDropdown) {
    return (
      <div className="group relative flex items-center" key={link.label}>
        {/* "Services" is a genuine link to /services; the adjacent chevron
            button pins the dropdown for keyboard/touch users. Mouse reveal is
            CSS group-hover; the pinned state is the only JS driver, so Escape
            always closes the menu (pinned=false + blur, no focus-within). */}
        <NavLink
          to={link.href}
          data-testid="nav-link-services"
          onClick={() => setMenuOpen(false)}
          className={({ isActive }) =>
            cn(
              base,
              isActive ? 'text-navy-900' : 'text-navy-900/65 hover:text-navy-900',
              'after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:origin-left after:bg-gold-600 after:transition-transform after:duration-300',
              isActive
                ? 'after:scale-x-100'
                : 'after:scale-x-0 group-hover:after:scale-x-100'
            )
          }
        >
          Services
        </NavLink>
        <button
          type="button"
          aria-label="Open services menu"
          aria-expanded={menuOpen}
          aria-haspopup="true"
          onClick={() => setMenuOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setMenuOpen(false);
              e.currentTarget.blur();
            }
          }}
          data-testid="nav-services-toggle"
          className="ml-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md text-navy-900/65 transition-colors hover:bg-navy-50 hover:text-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
        >
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-200',
              menuOpen && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>
        <div
          className={cn(
            'absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 transition-[opacity,transform] duration-200',
            menuOpen
              ? 'visible opacity-100'
              : 'invisible opacity-0 group-hover:visible group-hover:opacity-100'
          )}
        >
          <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-card" role="menu" aria-label="Services menu">
            <Link
              to="/services"
              data-testid="nav-dropdown-overview"
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-navy-900/75 transition-colors hover:bg-navy-50 hover:text-navy-900"
              onClick={() => setMenuOpen(false)}
            >
              <ServiceMark color="#9C7400" className="h-6 w-6 shrink-0" />
              Services Overview
            </Link>
            {SERVICES.map((s) => (
              <Link
                key={s.id}
                to={s.href}
                data-testid={`nav-dropdown-${s.id}`}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-navy-900/75 transition-colors hover:bg-navy-50 hover:text-navy-900"
                onClick={() => setMenuOpen(false)}
              >
                <ServiceMark color={s.color} className="h-6 w-6 shrink-0" />
                {s.name}
              </Link>
            ))}
            <Link
              to="/ecosystem"
              data-testid="nav-dropdown-ecosystem"
              className="mt-1 flex items-center gap-3 rounded-lg border-t border-slate-100 px-3 py-2.5 text-sm font-semibold text-navy-900/75 transition-colors hover:bg-navy-50 hover:text-navy-900"
              onClick={() => setMenuOpen(false)}
            >
              <ServiceMark color="#0B1729" className="h-6 w-6 shrink-0" />
              Ecosystem
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <NavLink
      key={link.label}
      to={link.href}
      end={link.href === '/'}
      data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={({ isActive }) =>
        cn(
          base,
          isActive ? 'text-navy-900' : 'text-navy-900/65 hover:text-navy-900',
          'after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:origin-left after:bg-gold-600 after:transition-transform after:duration-300',
          isActive ? 'after:scale-x-100' : 'after:scale-x-0 hover:after:scale-x-100'
        )
      }
    >
      {link.label}
    </NavLink>
  );
};

export const Header = () => {
  useLiveTick();
  const NAV_LINKS = getNavLinks();
  const CONTACT = getContact();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (open) {
      stopScroll();
      closeRef.current?.focus();
      const onKey = (e) => {
        if (e.key === 'Escape') setOpen(false);
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }
    startScroll();
  }, [open]);

  useEffect(() => {
    if (!open) triggerRef.current = triggerRef.current;
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300',
        scrolled
          ? 'border-b border-slate-200 bg-white/95 shadow-[0_6px_24px_-12px_rgba(11,21,40,0.25)] backdrop-blur-md'
          : 'border-b border-transparent bg-white'
      )}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" aria-label="Amden Group — home" className="shrink-0" data-testid="header-logo">
          <img
            src="/assets/amden-logo-dark.png"
            alt="Amden Group — Advice that guides. Solutions that deliver."
            width="188"
            height="44"
            className="h-10 w-auto sm:h-11"
          />
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-6 lg:flex xl:gap-8">
          {NAV_LINKS.map((link) => (
            <DesktopLink key={link.label} link={link} />
          ))}
          <Link
            to="/#solution-finder"
            data-testid="header-get-started-btn"
            className="group inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-navy-700"
          >
            Get Started
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        </nav>

        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label="Open menu"
          data-testid="mobile-menu-button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 text-navy-900 transition-colors hover:bg-navy-50 lg:hidden"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {/* Rendered into document.body, NOT inside <header>. The header gets
          `backdrop-blur-md` once scrolled, and backdrop-filter promotes an
          element to the containing block for its position:fixed descendants —
          which collapsed this `fixed inset-0` overlay to the header's 80px box
          and stacked its children on top of the nav links. Portalling keeps
          inset-0 resolving against the viewport in every scroll state. */}
      {open &&
        createPortal(
          <div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-navy-950 lg:hidden"
          >
            <div className="flex h-20 items-center justify-between px-4 sm:px-6">
              <img
                src="/assets/amden-logo-white.png"
                alt="Amden Group"
                width="188"
                height="44"
                className="h-10 w-auto"
              />
              <button
                ref={closeRef}
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-white/15 text-white transition-colors hover:bg-white/10"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav aria-label="Mobile" className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
              <ul className="space-y-1">
                {NAV_LINKS.map((link) =>
                  link.hasDropdown ? (
                    <li key={link.label}>
                      <div className="flex items-center">
                        <Link
                          to={link.href}
                          onClick={() => setOpen(false)}
                          data-testid="mobile-nav-link-services-overview"
                          className="flex flex-1 items-center justify-between rounded-lg px-3 py-4 text-lg font-bold text-white transition-colors hover:bg-white/5"
                        >
                          Services Overview
                          <ArrowRight className="h-4 w-4 text-gold-500" aria-hidden="true" />
                        </Link>
                        <button
                          type="button"
                          aria-label="Show service areas"
                          aria-expanded={mobileServicesOpen}
                          onClick={() => setMobileServicesOpen((o) => !o)}
                          data-testid="mobile-services-toggle"
                          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-white/15 text-white transition-colors hover:bg-white/10"
                        >
                          <ChevronDown
                            className={cn('h-4 w-4 transition-transform duration-200', mobileServicesOpen && 'rotate-180')}
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                      {mobileServicesOpen && (
                        <ul className="mt-1 space-y-1 border-l border-white/10 pl-4" data-testid="mobile-services-submenu">
                          {SERVICES.map((s) => (
                            <li key={s.id}>
                              <Link
                                to={s.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${s.id}`}
                                className="flex items-center justify-between rounded-lg px-3 py-3 text-base font-semibold text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
                              >
                                {s.name}
                                <ArrowRight className="h-4 w-4 text-gold-500" aria-hidden="true" />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ) : (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className="flex items-center justify-between rounded-lg px-3 py-4 text-lg font-bold text-white transition-colors hover:bg-white/5"
                      >
                        {link.label}
                        <ArrowRight className="h-4 w-4 text-gold-500" aria-hidden="true" />
                      </Link>
                    </li>
                  )
                )}
              </ul>
              <Link
                to="/#solution-finder"
                onClick={() => setOpen(false)}
                data-testid="mobile-get-started-btn"
                className="mt-6 flex items-center justify-center gap-2 rounded-md bg-gold-600 px-5 py-4 text-base font-bold text-navy-950 transition-colors hover:bg-gold-500"
              >
                Get Started
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </nav>
            <div className="border-t border-white/10 px-4 py-5 sm:px-6">
              <div className="flex flex-col gap-2 text-sm text-slate-300">
                <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 transition-colors hover:text-white">
                  <Phone className="h-4 w-4 text-gold-500" aria-hidden="true" /> {CONTACT.phone}
                </a>
                <a href={CONTACT.emailHref} className="inline-flex items-center gap-2 transition-colors hover:text-white">
                  <Mail className="h-4 w-4 text-gold-500" aria-hidden="true" /> {CONTACT.email}
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
};
